import { useEffect } from "react";
import api from "@/lib/api";

const DEFAULTS = { "--brand": "#09CF83", "--brand-hover": "#08B874", "--brand-light": "#E7FBF2", "--brand-border": "#A7EBCB", "--brand-dark": "#06734A", "--brand-link": "#06734A", "--brand-foreground": "#073B2A" };

function luminance(hex) {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  const ch = (v) => { v /= 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; };
  return 0.2126 * ch((n >> 16) & 255) + 0.7152 * ch((n >> 8) & 255) + 0.0722 * ch(n & 255);
}

// Dark ink or white, whichever reads better on the given fill.
export function readableOn(hex) {
  const l = luminance(hex);
  return (l + 0.05) / (luminance("#073B2A") + 0.05) >= 1.05 / (l + 0.05) ? "#073B2A" : "#FFFFFF";
}

// Darken/lighten a hex by a factor (for hover/dark/light derived tokens).
function shade(hex, f) {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  const clamp = (v) => Math.max(0, Math.min(255, Math.round(v)));
  let r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  if (f < 0) { r *= 1 + f; g *= 1 + f; b *= 1 + f; }
  else { r += (255 - r) * f; g += (255 - g) * f; b += (255 - b) * f; }
  return "#" + [clamp(r), clamp(g), clamp(b)].map((v) => v.toString(16).padStart(2, "0")).join("");
}

// Centralised token application — never scatters hex through components. Safety/status colours
// (destructive/red/amber) are intentionally NOT themed.
export function applyBranding(b) {
  const root = document.documentElement;
  // Uncustomised organisations follow the CaterCTRL default theme; customer palettes are preserved.
  if (!b || !b.primary_colour || b.is_customised === false) { Object.entries(DEFAULTS).forEach(([k, v]) => root.style.setProperty(k, v)); return; }
  const primary = b.primary_colour;
  root.style.setProperty("--brand", primary);
  root.style.setProperty("--brand-hover", shade(primary, -0.12));
  root.style.setProperty("--brand-dark", shade(primary, -0.4));
  root.style.setProperty("--brand-light", shade(primary, 0.85));
  root.style.setProperty("--brand-border", shade(primary, 0.55));
  root.style.setProperty("--brand-link", shade(primary, -0.4));
  root.style.setProperty("--brand-foreground", readableOn(primary));
}

// Runtime theming hook — mount once inside the authenticated shell.
export function useApplyBranding() {
  useEffect(() => {
    let alive = true;
    api.get("/branding/me")
      .then((r) => { if (alive) { applyBranding(r.data); window.__ccBranding = r.data; window.dispatchEvent(new Event("cc-branding-loaded")); } })
      .catch(() => applyBranding(null));
    return () => { alive = false; };
  }, []);
}
